import React from "react";
import { Icon } from "@fluentui/react";
import styles from "./Sidebar.module.css";
import { SignedIn, SignedOut, SignInButton, SignOutButton, UserButton, useUser } from "@clerk/clerk-react";
import Notifications from "../Notifications/Notifications";
import { CopilotIndicator, CopilotInfo } from "../CopilotIndicator";

interface QuotaInfo {
    queriesUsed: number;
    queriesLimit: number | null;
    isUnlimited: boolean;
    canQuery: boolean;
    planId?: string;
}

interface SidebarProps {
    onClearChat: () => void;
    isClearChatDisabled: boolean;
    onCopilotClick: () => void;
    onCopilotClear?: () => void;
    selectedCopilot?: CopilotInfo | null;
    onContextClick: () => void;
    onSavedCarsClick?: () => void;
    savedCarsCount?: number;
    onOportunidadesClick?: () => void;
    onHistoryClick?: () => void;
    className?: string;
    // Subscription props
    quotaInfo?: QuotaInfo;
    justUpgraded?: boolean;
    onUpgradeClick?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
    onClearChat,
    isClearChatDisabled,
    onCopilotClick,
    onCopilotClear,
    selectedCopilot,
    onContextClick,
    onSavedCarsClick,
    savedCarsCount,
    onOportunidadesClick,
    onHistoryClick,
    className,
    quotaInfo,
    justUpgraded,
    onUpgradeClick
}) => {

    // Optional: use user info for telemetry later
    const { user, isSignedIn } = useUser();

    return (
        <div className={`${styles.sidebar} ${className || ''}`}>
            <div className={styles.logoSection}>
                <img src="/favicon.png" alt="Videntis logo" className={styles.logo} />
                <div className={styles.transparencyBadge}>
                    <svg className={styles.transparencyIcon} width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L3 7V12C3 17.55 6.84 22.74 12 24C17.16 22.74 21 17.55 21 12V7L12 2ZM10 17L6 13L7.41 11.59L10 14.17L16.59 7.58L18 9L10 17Z" fill="currentColor"/>
                    </svg>
                    <span>Sin comisiones</span>
                    <span className={styles.transparencyTooltip}>Importante: No recibimos comisiones de vendedores ni concesionarios. Nuestras recomendaciones son 100% imparciales.</span>
                </div>
            </div>

            <div className={styles.mainButtons}>
                <button
                    className={`${styles.addButton} ${styles.active}`}
                    onClick={onClearChat}
                    disabled={isClearChatDisabled}
                >
                    <Icon iconName="Add" />
                    <span className={styles.buttonLabel}>Nuevo Chat</span>
                </button>

                {onHistoryClick && (
                    <button
                        className={styles.navButton}
                        onClick={onHistoryClick}
                    >
                        <Icon iconName="History" />
                        <span className={styles.buttonLabel}>Historial</span>
                    </button>
                )}

                <button
                    className={styles.navButton}
                    onClick={onCopilotClick}
                >
                    <Icon iconName="Robot" />
                    <span className={styles.buttonLabel}>Elige tu Copiloto</span>
                </button>

                <button
                    className={styles.navButton}
                    onClick={onContextClick}
                >
                    <Icon iconName="FavoriteStar" className={styles.yellowStar} />
                    <span className={styles.buttonLabel}>Añadir contexto</span>
                </button>

                {onSavedCarsClick && (
                    <button
                        className={styles.navButton}
                        onClick={onSavedCarsClick}
                    >
                        <Icon iconName="Save" />
                        <span className={styles.buttonLabel}>
                            Autos guardados{savedCarsCount ? ` (${savedCarsCount})` : ''}
                        </span>
                    </button>
                )}

                {onOportunidadesClick && (
                    <button
                        className={styles.navButton}
                        onClick={onOportunidadesClick}
                    >
                        <span style={{ fontSize: '16px', marginRight: '2px' }}>🔥</span>
                        <span className={styles.buttonLabel}>
                            Oportunidades
                        </span>
                    </button>
                )}

                <button
                    className={styles.navButton}
                    onClick={() => {
                        window.open('https://www.videntis.ai', '_blank');
                    }}
                >
                    <Icon iconName="Info" />
                    <span className={styles.buttonLabel}>Nosotros</span>
                </button>

            </div>

            {/* Selected Copilot Section */}
            {selectedCopilot && (
                <div className={styles.copilotSection}>
                    <div className={styles.copilotCard}>
                        <div className={styles.copilotHeader}>
                            <img
                                src={selectedCopilot.avatar}
                                alt={selectedCopilot.name}
                                className={styles.copilotAvatar}
                            />
                            <div className={styles.copilotInfo}>
                                <div className={styles.copilotName}>{selectedCopilot.name}</div>
                            </div>
                        </div>
                        <div className={styles.copilotActions}>
                            <button
                                className={styles.copilotChangeBtn}
                                onClick={onCopilotClick}
                                aria-label="Cambiar copiloto"
                                title="Cambiar copiloto"
                            >
                                <Icon iconName="Switch" style={{ fontSize: '12px' }} />
                                <span className={styles.copilotBtnText}>Cambiar</span>
                            </button>
                            {onCopilotClear && (
                                <button
                                    className={styles.copilotClearBtn}
                                    onClick={onCopilotClear}
                                    aria-label="Desactivar copiloto"
                                    title="Desactivar copiloto"
                                >
                                    <Icon iconName="Cancel" style={{ fontSize: '12px' }} />
                                    <span className={styles.copilotBtnText}>Cerrar</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div className={styles.bottomButtons}>
                {/* Subscription Status - PRO Badge or Upgrade Button */}
                {quotaInfo && (
                    quotaInfo.isUnlimited ? (
                        <div className={styles.proBadge}>
                            <svg className={styles.proBadgeIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                            </svg>
                            <span>PRO</span>
                            {justUpgraded && <span className={styles.proBadgeNew}>nuevo</span>}
                        </div>
                    ) : (
                        <button className={styles.upgradePlanButton} onClick={onUpgradeClick}>
                            <svg className={styles.upgradePlanIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M12 18C10.5 18 9.3 17.1 9 16C8.7 17.1 7.5 18 6 18C4.3 18 3 16.7 3 15C3 13.3 4.3 12 6 12C7.5 12 8.7 12.9 9 14C9.3 12.9 10.5 12 12 12C13.7 12 15 13.3 15 15C15 16.7 13.7 18 12 18Z" fill="currentColor"/>
                                <path d="M18 18C16.3 18 15 16.7 15 15C15 13.3 16.3 12 18 12C19.7 12 21 13.3 21 15C21 16.7 19.7 18 18 18Z" fill="currentColor"/>
                                <circle cx="8" cy="8" r="2.5" fill="currentColor"/>
                                <circle cx="16" cy="8" r="2.5" fill="currentColor"/>
                            </svg>
                            <span>Subí de plan</span>
                            {quotaInfo.queriesLimit && (
                                <span className={styles.quotaCounter}>
                                    {quotaInfo.queriesUsed}/{quotaInfo.queriesLimit}
                                </span>
                            )}
                        </button>
                    )
                )}

                <SignedOut>
                    <SignInButton mode="modal">
                        <button className={styles.navButton}>
                            <Icon iconName="Contact" />
                            <span className={styles.buttonLabel}>Iniciar sesi&oacute;n</span>
                        </button>
                    </SignInButton>
                </SignedOut>

                <SignedIn>
                    <Notifications className={styles.navButton} labelClassName={styles.buttonLabel} />
                    
                    <div className={styles.navButton} style={{ justifyContent: "flex-start" }}>
                        <UserButton
                            appearance={{
                                elements: {
                                    userButtonAvatarBox: { width: 24, height: 24 },
                                    userButtonTrigger: { padding: 0 }
                                }
                            }}
                            userProfileMode="modal"
                            afterSignOutUrl="/"
                        />
                        <span className={styles.buttonLabel} style={{ marginLeft: 12 }}>
                            {user?.firstName || user?.username || "Cuenta"}
                        </span>
                    </div>
                    
                    <SignOutButton>
                        <button className={styles.navButton}>
                            <Icon iconName="SignOut" />
                            <span className={styles.buttonLabel}>Cerrar sesi&oacute;n</span>
                        </button>
                    </SignOutButton>
                </SignedIn>

            </div>
        </div>
    );
};

export default Sidebar;